import {
    Alert,
    Box,
    Button,
    Collapse,
    FormControl,
    FormHelperText,
    Grid,
    Grow,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import dayjs from "dayjs";
import * as yup from "yup";

import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import { EditionDeedProps } from "@interfaces/slices";

import "dayjs/locale/es-mx";
import { inputOnlyAlpha } from "@helpers/generals";
import { useGetStatesQuery, useLazyGetLocalitiesByStateQuery } from "@services/api/catalogs";
import { useLazyGetDeedQuery, usePatchDeedMutation } from "@services/api/dictamen";
import { useEffect, useState } from "react";
import { Locality } from "@interfaces/postalCodes";

export const EdicionEscritura = (props: EditionDeedProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [showSavingStatus, setShowSavingStatus] = useState<boolean>(false);

    const [localitiesList, setLocalitiesList] = useState<Array<Locality>>([]);
    const [localitiesListRRPC, setLocalitiesListRRPC] = useState<Array<Locality>>([]);

    const statesPlaces = useGetStatesQuery();

    const [triggerGetDeed] = useLazyGetDeedQuery();
    const [triggerPatchDeed, { isSuccess: isPatchSuccess }] = usePatchDeedMutation();
    const [triggerGetLocalitiesByState] = useLazyGetLocalitiesByStateQuery();

    const formik = useFormik({
        initialValues: {
            public_deed_date: "",
            notary_office_number: "",
            full_name_notary: "",
            municipality: "",
            federative_organization: "",
            commercial_folio_number: "",
            registration_date: "",
            federative_organization_RRPC: "",
            municipality_RPPC: "",
        },
        validationSchema: yup.object().shape({
            public_deed_date: yup.date().required("Campo requerido"),
            notary_office_number: yup.number().required("Campo requerido"),
            full_name_notary: yup.string().required("Campo requerido"),
            municipality: yup.number().required("Campo requerido"),
            federative_organization: yup.number().required("Campo requerido"),
            commercial_folio_number: yup.string().notRequired(),
            registration_date: yup.date().notRequired(),
            federative_organization_RRPC: yup.number().notRequired(),
            municipality_RPPC: yup.number().notRequired(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await triggerPatchDeed({
                    deedId: props.deed.id,
                    body: {
                        ...values,
                        registration_date: values.registration_date.length === 0 ? null : values.registration_date,
                        federative_organization_RRPC:
                            values.federative_organization_RRPC.length === 0
                                ? null
                                : values.federative_organization_RRPC,
                        municipality_RPPC: values.municipality_RPPC.length === 0 ? null : values.municipality_RPPC,
                    },
                }).unwrap();

                setShowSavingStatus(true);
                setTimeout(() => {
                    formik.resetForm({ values: values });
                    setShowSavingStatus(false);
                }, 1500);
            } catch (error) {
                console.log(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleOnChangeState = async (event: SelectChangeEvent) => {
        formik.handleChange(event);

        try {
            const localities = await triggerGetLocalitiesByState(parseInt(event.target.value)).unwrap();
            setLocalitiesList(localities);
            await formik.setFieldValue("municipality", "", false);
            formik.setFieldError("municipality", "");
        } catch (error) {
            console.log("Error al consultar las localidades");
        }
    };

    const handleOnChangeStateRRPC = async (event: SelectChangeEvent) => {
        formik.handleChange(event);

        try {
            const localities = await triggerGetLocalitiesByState(parseInt(event.target.value)).unwrap();
            setLocalitiesListRRPC(localities);
            await formik.setFieldValue("municipality_RPPC", "", false);
            formik.setFieldError("municipality_RPPC", "");
        } catch (error) {
            console.log("Error al consultar las localidades");
        }
    };

    const loadInfo = async () => {
        try {
            setLoading(true);

            const deedInfo = await triggerGetDeed(props.deed.id).unwrap();

            if (deedInfo.federative_organization) {
                const localities = await triggerGetLocalitiesByState(Number(deedInfo.federative_organization)).unwrap();
                setLocalitiesList(localities);
            }

            if (deedInfo.federative_organization_RRPC) {
                const localitiesRRPC = await triggerGetLocalitiesByState(
                    Number(deedInfo.federative_organization_RRPC),
                ).unwrap();
                setLocalitiesListRRPC(localitiesRRPC);
            }

            formik.resetForm({
                values: {
                    public_deed_date: deedInfo.public_deed_date,
                    notary_office_number: deedInfo.notary_office_number,
                    full_name_notary: deedInfo.full_name_notary,
                    municipality: deedInfo.municipality.toString(),
                    federative_organization: deedInfo.federative_organization ?? "",
                    commercial_folio_number: deedInfo.commercial_folio_number,
                    registration_date: deedInfo.registration_date,
                    federative_organization_RRPC: deedInfo.federative_organization_RRPC ?? "",
                    municipality_RPPC: deedInfo.municipality_RPPC,
                },
            });
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        formik.resetForm();
        loadInfo();
    }, [props.deed]);

    return (
        <Box maxHeight={"800px"} maxWidth={props.width} my={2}>
            <Box maxHeight={"800px"} overflow={"auto"} py={2}>
                <Stack px={2} rowGap={2}>
                    <Typography variant="h5" fontWeight={600} color="main3">
                        Datos Escritura {props.deed.public_deed_number}
                    </Typography>
                    <form onSubmit={formik.handleSubmit} noValidate>
                        <Stack pl={2} rowGap={1}>
                            <Typography variant="h6" fontWeight={600} color="main3">
                                Datos escritura
                            </Typography>
                            <Box>
                                <FormControl>
                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
                                        <DatePicker
                                            name="public_deed_date"
                                            format="DD/MM/YYYY"
                                            disableFuture
                                            label="Fecha de escritura pública"
                                            value={
                                                dayjs(formik.values.public_deed_date).isValid()
                                                    ? dayjs(formik.values.public_deed_date)
                                                    : null
                                            }
                                            onChange={(date) =>
                                                formik.setFieldValue(
                                                    "public_deed_date",
                                                    dayjs(date).format("YYYY-MM-DD"),
                                                )
                                            }
                                            slotProps={{
                                                textField: {
                                                    required: true,
                                                    name: "public_deed_date",
                                                    size: "small",

                                                    error:
                                                        formik.touched.public_deed_date &&
                                                        Boolean(formik.errors.public_deed_date),
                                                    helperText:
                                                        formik.touched.public_deed_date &&
                                                        formik.errors.public_deed_date,
                                                    onBlur: formik.handleBlur,
                                                },
                                            }}
                                            disabled={formik.isSubmitting || loading || props.readonly}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Box>

                            <Typography variant="h6" fontWeight={600} color="main3" mt={1}>
                                Datos de notaría
                            </Typography>
                            <Grid container columnGap={1}>
                                <FormControl margin="normal" sx={{ minWidth: "150px" }}>
                                    <NumericFormat
                                        name="notary_office_number"
                                        label={"No. de Notaria"}
                                        required
                                        displayType="input"
                                        autoComplete="off"
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: 10,
                                            },
                                            input: {
                                                size: "small",
                                            },
                                            inputLabel: {
                                                size: "small",
                                            },
                                        }}
                                        customInput={TextField}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        error={
                                            formik.touched.notary_office_number &&
                                            Boolean(formik.errors.notary_office_number)
                                        }
                                        helperText={
                                            formik.touched.notary_office_number && formik.errors.notary_office_number
                                        }
                                        value={formik.values.notary_office_number}
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    />
                                </FormControl>
                                <FormControl margin="normal" sx={{ minWidth: "250px" }} disabled={props.readonly}>
                                    <TextField
                                        id="full_name_notary"
                                        name="full_name_notary"
                                        label="Nombre completo notario"
                                        size="small"
                                        required
                                        autoComplete="off"
                                        onBlur={formik.handleBlur}
                                        onChange={(event) => inputOnlyAlpha(event, formik)}
                                        error={
                                            formik.touched.full_name_notary && Boolean(formik.errors.full_name_notary)
                                        }
                                        helperText={formik.touched.full_name_notary && formik.errors.full_name_notary}
                                        value={formik.values.full_name_notary}
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    />
                                </FormControl>
                                <FormControl
                                    margin="normal"
                                    sx={{ minWidth: "250px" }}
                                    size="small"
                                    disabled={props.readonly}
                                >
                                    <InputLabel required>Entidad federativa</InputLabel>
                                    <Select
                                        id="federative_organization"
                                        name="federative_organization"
                                        label={"Entidad federativa"}
                                        size="small"
                                        value={statesPlaces.isSuccess ? formik.values.federative_organization : ""}
                                        onBlur={formik.handleBlur}
                                        onChange={handleOnChangeState}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                                        error={
                                            formik.touched.federative_organization &&
                                            Boolean(formik.errors.federative_organization)
                                        }
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    >
                                        {statesPlaces.isSuccess &&
                                            statesPlaces.currentData?.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                    {formik.touched.federative_organization &&
                                        Boolean(formik.errors.federative_organization) && (
                                            <FormHelperText sx={{ color: "#d32f2f" }}>
                                                {formik.errors.federative_organization}
                                            </FormHelperText>
                                        )}
                                </FormControl>
                                <FormControl
                                    margin="normal"
                                    sx={{ minWidth: "250px" }}
                                    size="small"
                                    disabled={props.readonly}
                                >
                                    <InputLabel required>Municipio</InputLabel>
                                    <Select
                                        id="municipality"
                                        name="municipality"
                                        label="Municipio"
                                        size="small"
                                        value={localitiesList.length !== 0 ? formik.values.municipality : ""}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                                        error={formik.touched.municipality && Boolean(formik.errors.municipality)}
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    >
                                        {localitiesList.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.municipality && Boolean(formik.errors.municipality) && (
                                        <FormHelperText sx={{ color: "#d32f2f" }}>
                                            {formik.errors.municipality}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Typography variant="h6" fontWeight={600} color="main3" mt={1}>
                                Datos de Inscripción
                            </Typography>
                            <Grid container columnGap={1}>
                                <FormControl margin="normal" sx={{ minWidth: "150px" }} disabled={props.readonly}>
                                    <TextField
                                        id="commercial_folio_number"
                                        name="commercial_folio_number"
                                        label="No. de Folio Mercantil"
                                        size="small"
                                        // required
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: 50,
                                            },
                                        }}
                                        autoComplete="off"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.commercial_folio_number}
                                        error={
                                            formik.touched.commercial_folio_number &&
                                            Boolean(formik.errors.commercial_folio_number)
                                        }
                                        helperText={
                                            formik.touched.commercial_folio_number &&
                                            formik.errors.commercial_folio_number
                                        }
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    />
                                </FormControl>
                                <FormControl margin="normal" sx={{ minWidth: "160px" }} disabled={props.readonly}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
                                        <DatePicker
                                            name="registration_date"
                                            format="DD/MM/YYYY"
                                            disableFuture
                                            label="Fecha de Inscripción"
                                            value={
                                                dayjs(formik.values.registration_date).isValid()
                                                    ? dayjs(formik.values.registration_date)
                                                    : null
                                            }
                                            onChange={(date) =>
                                                formik.setFieldValue(
                                                    "registration_date",
                                                    dayjs(date).format("YYYY-MM-DD"),
                                                )
                                            }
                                            slotProps={{
                                                textField: {
                                                    // required: true,
                                                    name: "registration_date",
                                                    size: "small",
                                                    error:
                                                        formik.touched.registration_date &&
                                                        Boolean(formik.errors.registration_date),
                                                    helperText:
                                                        formik.touched.registration_date &&
                                                        formik.errors.registration_date,
                                                    onBlur: formik.handleBlur,
                                                },
                                            }}
                                            disabled={formik.isSubmitting || loading || props.readonly}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                                <FormControl
                                    margin="normal"
                                    sx={{ minWidth: "250px" }}
                                    size="small"
                                    disabled={props.readonly}
                                >
                                    <InputLabel>Entidad federativa de RRPC</InputLabel>
                                    <Select
                                        id="federative_organization_RRPC"
                                        name="federative_organization_RRPC"
                                        label={"Entidad federativa de RRPC"}
                                        size="small"
                                        value={statesPlaces.isSuccess ? formik.values.federative_organization_RRPC : ""}
                                        onBlur={formik.handleBlur}
                                        onChange={handleOnChangeStateRRPC}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                                        error={
                                            formik.touched.federative_organization_RRPC &&
                                            Boolean(formik.errors.federative_organization_RRPC)
                                        }
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    >
                                        {statesPlaces.isSuccess &&
                                            statesPlaces.currentData?.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                    {formik.touched.federative_organization_RRPC &&
                                        Boolean(formik.errors.federative_organization_RRPC) && (
                                            <FormHelperText sx={{ color: "#d32f2f" }}>
                                                {formik.errors.federative_organization_RRPC}
                                            </FormHelperText>
                                        )}
                                </FormControl>
                                <FormControl
                                    margin="normal"
                                    sx={{ minWidth: "250px" }}
                                    size="small"
                                    disabled={props.readonly}
                                >
                                    <InputLabel>Municipio</InputLabel>
                                    <Select
                                        id="municipality_RPPC"
                                        name="municipality_RPPC"
                                        label="Municipio RRPC"
                                        size="small"
                                        value={localitiesListRRPC.length !== 0 ? formik.values.municipality_RPPC : ""}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                                        error={
                                            formik.touched.municipality_RPPC && Boolean(formik.errors.municipality_RPPC)
                                        }
                                        disabled={formik.isSubmitting || loading || props.readonly}
                                    >
                                        {localitiesListRRPC.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.municipality_RPPC && Boolean(formik.errors.municipality_RPPC) && (
                                        <FormHelperText sx={{ color: "#d32f2f" }}>
                                            {formik.errors.municipality_RPPC}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Stack>
                        <Box>
                            <Collapse in={formik.dirty}>
                                <Box
                                    mt={2}
                                    display={"flex"}
                                    flexDirection={"row"}
                                    justifyContent={"end"}
                                    alignItems={"center"}
                                    columnGap={2}
                                >
                                    <Grow in={showSavingStatus}>
                                        <Alert color={isPatchSuccess === true ? "success" : "error"}>
                                            {isPatchSuccess
                                                ? "Datos de Escritura guardados"
                                                : "Ocurrio un error al guardar la información"}
                                        </Alert>
                                    </Grow>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="secondary4"
                                        loading={formik.isSubmitting || loading || props.readonly}
                                    >
                                        Guardar cambios
                                    </Button>
                                </Box>
                            </Collapse>
                        </Box>
                    </form>
                </Stack>
            </Box>
        </Box>
    );
};
